import { TabView } from 'components/Tabs/TabView';
import { memo } from 'react';
import type { FC } from 'react';
import { IntentVerificationList } from './IntentVerificationList';
import './styles.css';
import { isIntentGraphOverlay } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import type { IntentVerificationTabId } from 'components/Graph/GraphSidePanelData';
import { showSidePanel } from 'components/Graph/commands/showSidePanel';
import { selectGraphMode } from 'components/Graph/model/selectors/graphMetadata/selectGraphMode';
import { RecoverableLoader } from 'components/RecoverableLoader/RecoverableLoader';
import { TabLabelWithReportSeverityBadge } from 'components/TabLabelWithBadge/TabLabelWithReportSeverityBadge';
import { useLocale } from 'containers/App/LocaleContext';
import { BuiltInRulesList } from './BuiltInRulesList';
import { GraphPathChecks } from './GraphPathChecks';
import { useIntentVerification } from './useIntentVerification';
import { usePathChecks } from './usePathChecks';

const BareIntentVerification: FC = () => {
  const msg = useLocale();
  const ctxProps = useGraphContextSelectors({
    graphMode: selectGraphMode,
    dispatch: prop('dispatch'),
    tabId: prop('sidePanel', 'intentVerificationTabId'),
    overlay: prop('overlay'),
  });
  const pathChecksHook = usePathChecks();
  const intentVerificationHook = useIntentVerification();

  const selectedIntentRuleId = isIntentGraphOverlay(ctxProps.overlay)
    ? ctxProps.overlay.intentRule.id
    : null;

  const tabs = [
    {
      key: 'defined-rules',
      label: (
        <TabLabelWithReportSeverityBadge
          badge={intentVerificationHook.definedRules.summary?.badge}
          className='-mx-lgu-2'
          text={msg.canvasGraph.graphSidePanel.intentVerification.definedRules}
          loading={
            intentVerificationHook.definedRules.loadingState === 'LOADING'
          }
        />
      ),

      render: () => (
        <RecoverableLoader
          errorClassName='mt-lgu-10'
          loadingState={intentVerificationHook.definedRules.loadingState}
          onRecover={intentVerificationHook.loadIntents}
        >
          <IntentVerificationList
            data={intentVerificationHook.definedRules}
            selectedIntentRuleId={selectedIntentRuleId}
          />
        </RecoverableLoader>
      ),
    },
    {
      disabled: ctxProps.graphMode === 'pathLookup',
      key: 'builtin-rules',
      label: (
        <TabLabelWithReportSeverityBadge
          badge={intentVerificationHook.builtInRules.summary?.badge}
          text={msg.canvasGraph.graphSidePanel.intentVerification.builtInRules}
          loading={
            intentVerificationHook.builtInRules.loadingState === 'LOADING'
          }
        />
      ),

      render: () => (
        <RecoverableLoader
          errorClassName='mt-lgu-10'
          loadingState={intentVerificationHook.builtInRules.loadingState}
          onRecover={intentVerificationHook.loadIntents}
        >
          <BuiltInRulesList
            data={intentVerificationHook.builtInRules}
            selectedIntentRuleId={selectedIntentRuleId}
          />
        </RecoverableLoader>
      ),
    },
    {
      key: 'path-checks',
      label: (
        <TabLabelWithReportSeverityBadge
          badge={pathChecksHook.badge}
          text={msg.canvasGraph.graphSidePanel.intentVerification.pathChecks}
          loading={pathChecksHook.loading}
        />
      ),

      render: () => <GraphPathChecks pathChecksHook={pathChecksHook} />,
    },
  ] as const;

  return (
    <div className='graph-side-panel-tabbed-content flex flex-1 flex-col'>
      <TabView<IntentVerificationTabId>
        activeTab={ctxProps.tabId}
        onChangeTab={(intentVerificationTabId) => {
          ctxProps.dispatch(showSidePanel, { intentVerificationTabId });
        }}
        stretch
        tabs={tabs}
        className='graph-side-panel-tabs-cards'
        contentClassName='flex flex-col flex-1'
        variant='legacy'
      />
    </div>
  );
};

export const IntentVerification = memo(BareIntentVerification);
